import store from '@/state/store'
import { Helper } from '@/mixin/helper'
import { hasLineOrderFeatureAccess } from '@/helpers/tms-line-order'
const hasAccessAll = store.getters['identity/hasAccessAll']
const hasTmsUserGroupAccess = hasAccessAll(['tms-user-group:read']) && Helper.methods.isSuperUser()
const hasTmsColdChainAccess = hasAccessAll(['tms-cold-chain:read'])
const hasLineOrderAccess = hasLineOrderFeatureAccess()

import BoxTypesChip from '../components/BoxTypesChip'

import * as Address from '@/services/rest-api/lib/delivery/address'
import * as Customer from '@/services/rest-api/lib/customer/customer'
import * as ProductCategory from '@/services/rest-api/lib/product/product-category'
import * as Delivery from '@/services/rest-api/lib/delivery/delivery-order'

import { GET_USER_GROUP } from '@/services/api/setting/user/userGroup'
import { GET_CITIES } from '@/services/api/master/address'

const fetchCities = async function (opts = {}) {
  const response = await this.$apollo.query({
    query: GET_CITIES,
    fetchPolicy: 'no-cache',
    variables: {
      filter: {
        pagination: {
          page: opts.page,
          show: opts.limit || 5,
        },
        search: opts.search || undefined,
      },
    },
  })
  const cities = response.data.data.cities

  return {
    metadata: {
      total_count: cities.pagination.total,
      page: cities.pagination.page,
    },
    data: {
      paginated_result: cities.data.map((city) => ({
        ...city,
        id: city.name,
      })),
    },
  }
}

const formatListPreview = (list = []) => {
  if (!Array.isArray(list) || list.length === 0) return '-'
  if (list.length <= 2) return list.join(', ')
  return `${list.slice(0, 2).join(', ')} +${list.length - 2}`
}

const getLineOrderData = async (opts = {}) => {
  const filters = Array.isArray(opts?.filters) ? [...opts.filters] : []

  const response = await Delivery.getLineOrder({
    ...opts,
    filters,
  })
  const paginatedResult = response?.data?.paginated_result || []

  return {
    metadata: {
      ...(response?.metadata || {}),
    },
    data: {
      ...(response?.data || {}),
      paginated_result: paginatedResult.map((item) => ({
        ...item,
        id: item.line_order,
        line_order_label: item.line_order,
        delivery_order_count: item.delivery_order_count,
        shipped_customers: item.customer_names,
        created_by: item.created_bys,
      })),
    },
    isPagination: true,
  }
}

const translate = (t, key, fallback, params = {}) => {
  if (typeof t !== 'function') return fallback

  const translated = t(key, params)

  return translated === key ? fallback : translated
}

const selectField = (t, key, fallback) =>
  `${translate(t, 'global.form.select', 'Pilih')} ${translate(t, key, fallback)}`

export function tabelFilterFields(instance, context = {}) {
  const { isCreating = true, isSingleProductCategory = false, userGroup = null } = context
  const userGroupId = userGroup?.id ?? null
  const t = instance?.$t?.bind(instance)

  return [
    {
      name: 'status_type',
      isShow: true,
      label: translate(t, 'fleetTask.fields.status', 'Status'),
      type: 'button-group',
      placeholder: selectField(t, 'fleetTask.fields.status', 'Status'),
    },
    {
      name: 'operational_day',
      isShow: true,
      label: translate(t, 'master.form.address.operationalDay', 'Hari Operasional'),
      type: 'choice-chips',
      multiple: true,
      allValue: 'all',
      options: [
        { value: 'all', label: translate(t, 'fleetTask.status.all', 'Semua') },
        { value: 'monday', label: translate(t, 'global.days.monday', 'Senin') },
        { value: 'tuesday', label: translate(t, 'global.days.tuesday', 'Selasa') },
        { value: 'wednesday', label: translate(t, 'global.days.wednesday', 'Rabu') },
        { value: 'thursday', label: translate(t, 'global.days.thursday', 'Kamis') },
        { value: 'friday', label: translate(t, 'global.days.friday', "Jum'at") },
        { value: 'saturday', label: translate(t, 'global.days.saturday', 'Sabtu') },
        { value: 'sunday', label: translate(t, 'global.days.sunday', 'Minggu') },
      ],
      value: ['all'],
    },
    {
      name: 'user_group_id',
      isShow: hasTmsUserGroupAccess,
      label: translate(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
      type: 'value-help-v2',
      placeholder: selectField(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
      trackBy: 'id',
      displayedBy: 'name',
      disabled: !isCreating && userGroupId != null,
      listGetter: async function (opts) {
        let result = {}
        const filter = {
          pagination: {
            page: opts?.page,
            show: opts?.limit,
          },
          search: opts?.search,
        }

        const res = await this.$apollo.query({
          query: GET_USER_GROUP,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              ...filter,
            },
          },
        })

        const ugroups = res.data.ugroups.ugroups

        result = {
          metadata: {
            total_count: ugroups.pagination.total,
            page: ugroups.pagination.page,
          },
          data: {
            paginated_result: ugroups.data,
          },
        }

        return result
      },
      multiple: true,
      tableFields: [
        {
          label: translate(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
          key: 'name',
          isSortable: false,
        },
        {
          label: translate(t, 'delivery.fields.vehicleGroup', 'Grup Kendaraan'),
          key: 'vgroups',
          isSortable: false,
          formatter: (value) => {
            return value?.length != null
              ? value.length === 0
                ? translate(t, 'delivery.fields.allVehicleGroup', 'Semua Grup Kendaraan')
                : value.length
              : '-'
          },
        },
      ],
    },
    {
      name: 'expected_delivered_on',
      isShow: true,
      label: translate(t, 'salesOrder.fields.targetDeliveredOn', 'Target Terkirim'),
      type: 'date-picker',
      datepickerType: 'datetime',
      valueType: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
      format: 'DD MMM YYYY HH:mm',
      titleFormat: 'DD MMM YYYY',
      rangeSeparator: ' - ',
      range: true,
      confirm: true,
      confirmText: translate(t, 'global.action.apply', 'Terapkan'),
      shortcuts: [
        {
          text: translate(t, 'global.time.today', 'Hari ini'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setHours(0, 0, 0)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: translate(t, 'global.time.yesterday', 'Kemarin'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setDate(startDate.getDate() - 1)
            startDate.setHours(0, 0, 0)

            endDate.setDate(endDate.getDate() - 1)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: translate(t, 'global.time.last3Days', '3 Hari Terakhir'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setDate(startDate.getDate() - 3)
            startDate.setHours(0, 0, 0)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: translate(t, 'global.time.last7Days', '7 Hari Terakhir'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setDate(startDate.getDate() - 7)
            startDate.setHours(0, 0, 0)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
      ],
    },
    {
      name: 'line_order',
      isShow: hasLineOrderAccess,
      label: translate(t, 'delivery.fields.lineOrder', 'Line Order'),
      type: 'value-help-v2',
      placeholder: translate(t, 'delivery.placeholder.lineOrder', 'Pilih Line Order'),
      trackBy: 'id',
      displayedBy: 'line_order_label',
      listGetter: getLineOrderData,
      multiple: true,
      visibleTagCount: 2,
      canSelectAll: true,
      tableFields: [
        {
          label: translate(t, 'delivery.fields.lineOrder', 'Line Order'),
          key: 'line_order',
          isSortable: true,
        },
        {
          label: translate(t, 'delivery.fields.totalDelivery', 'Jumlah Pengiriman'),
          key: 'delivery_order_count',
          isSortable: true,
        },
        {
          label: translate(t, 'delivery.fields.shippedCustomerName', 'Pelanggan Penerima'),
          key: 'shipped_customers',
          isSortable: false,
          formatValue: (value) => formatListPreview(value),
        },
        {
          label: translate(t, 'delivery.fields.createdBy', 'Dibuat Oleh'),
          key: 'created_by',
          isSortable: false,
          formatValue: (value) => formatListPreview(value),
        },
      ],
    },
    {
      name: 'shipped_customer_id',
      isShow: true,
      label: translate(t, 'delivery.fields.shippedCustomerName', 'Pelanggan Penerima'),
      type: 'value-help-v2',
      placeholder: translate(t, 'delivery.placeholder.shippedCustomer', 'Pilih Pelanggan Penerima'),
      trackBy: 'id',
      displayedBy: 'name',
      listGetter: Customer.get,
      multiple: true,
      tableFields: [
        {
          label: translate(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
          key: 'name',
          isSortable: true,
        },
      ],
    },
    {
      name: 'origin_address_id',
      isShow: true,
      label: translate(t, 'delivery.fields.originAddress', 'Alamat Asal'),
      type: 'value-help-v2',
      placeholder: translate(t, 'delivery.placeholder.originAddress', 'Pilih Alamat Asal'),
      trackBy: 'id',
      displayedBy: 'name',
      listGetter: Address.get(),
      multiple: true,
      tableFields: [
        {
          label: translate(t, 'master.form.address.name', 'Nama Lokasi'),
          key: 'name',
          isSortable: true,
          isRedirectable: true,
        },
        {
          label: translate(t, 'master.form.address.city', 'Kota'),
          key: 'city',
          isSortable: true,
          isRedirectable: true,
        },
      ],
    },
    {
      name: 'destination_address_id',
      isShow: true,
      label: translate(t, 'delivery.fields.destinationAddress', 'Alamat Tujuan'),
      type: 'value-help-v2',
      placeholder: translate(t, 'delivery.placeholder.destinationAddress', 'Pilih Alamat Tujuan'),
      trackBy: 'id',
      displayedBy: 'name',
      listGetter: Address.get(),
      multiple: true,
      tableFields: [
        {
          label: translate(t, 'master.form.address.name', 'Nama Lokasi'),
          key: 'name',
          isSortable: true,
          isRedirectable: true,
        },
        {
          label: translate(t, 'master.form.address.city', 'Kota'),
          key: 'city',
          isSortable: true,
          isRedirectable: true,
        },
      ],
    },
    {
      name: 'destination_address_city',
      isShow: true,
      label: translate(t, 'delivery.fields.destinationCity', 'Kota Tujuan'),
      type: 'value-help-v2',
      placeholder: translate(t, 'delivery.placeholder.destinationCity', 'Pilih Kota Tujuan'),
      trackBy: 'id',
      displayedBy: 'name',
      listGetter: fetchCities,
      multiple: true,
      canSelectAll: true,
      tableFields: [
        {
          label: translate(t, 'master.form.address.regencyCity', 'Kota/Kabupaten'),
          key: 'name',
          isSortable: true,
        },
      ],
    },
    {
      name: 'product_category_id',
      isShow: hasAccessAll(['fleet-task-single-category-deliveries:read']),
      label: translate(t, 'fleetTask.fields.productCategory', 'Kategori Produk'),
      type: 'value-help-v2',
      placeholder: translate(t, 'delivery.placeholder.productCategory', 'Pilih Kategori Produk'),
      trackBy: 'id',
      displayedBy: 'name',
      listGetter: ProductCategory.get,
      multiple: true,
      canSelectAll: true,
      disabled: isSingleProductCategory,
      tableFields: [
        {
          label: translate(t, 'fleetTask.fields.productCategory', 'Kategori Produk'),
          key: 'name',
          isSortable: true,
          thStyle: 'width: 200px',
        },
        {
          label: translate(t, 'master.form.fleet.boxTypeV2', 'Jenis Box'),
          key: 'box_types',
          isSlot: true,
          slotComponent: BoxTypesChip,
        },
      ],
    },
    {
      type: 'range-input',
      label: translate(t, 'fleetTask.fields.totalWeight', 'Total Berat'),
      min: {
        name: 'load_weight_min',
        label: translate(t, 'fleetTask.fields.minimal', 'Minimal'),
        type: 'number',
        validation: 'bail|number|min:0,value',
        suffix: 'Kg',
        'validation-behavior': 'live',
      },
      max: {
        name: 'load_weight_max',
        label: translate(t, 'fleetTask.fields.maximal', 'Maksimal'),
        type: 'number',
        validation: 'bail|number|min:0,value',
        suffix: 'Kg',
        'validation-behavior': 'live',
      },
    },
    {
      type: 'range-input',
      label: translate(t, 'fleetTask.fields.totalVolume', 'Total Volume'),
      min: {
        name: 'load_volume_min',
        label: translate(t, 'fleetTask.fields.minimal', 'Minimal'),
        type: 'number',
        validation: 'bail|number|min:0,value',
        suffix: 'Cbm',
        'validation-behavior': 'live',
      },
      max: {
        name: 'load_volume_max',
        label: translate(t, 'fleetTask.fields.maximal', 'Maksimal'),
        type: 'number',
        validation: 'bail|number|min:0,value',
        suffix: 'Cbm',
        'validation-behavior': 'live',
      },
    },
    {
      type: 'range-input',
      isShow: hasTmsColdChainAccess,
      label: translate(t, 'delivery.filter.temperature', 'Temperatur'),
      min: {
        name: 'load_min_temperature',
        label: translate(t, 'fleetTask.fields.minimal', 'Minimal'),
        type: 'number',
        validation: 'bail|number',
        suffix: '°C',
        'validation-behavior': 'live',
      },
      max: {
        name: 'load_max_temperature',
        label: translate(t, 'fleetTask.fields.maximal', 'Maksimal'),
        type: 'number',
        validation: 'bail|number',
        suffix: '°C',
        'validation-behavior': 'live',
      },
    },
  ]
}
